import { create } from 'zustand';

type Theme = 'light' | 'dark';

interface UIState {
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;
  /** Right-side sheets/drawers that should hide the global floating AI CTA. */
  sidePanelOpenCount: number;
  theme: Theme;
  toggleSidebar: () => void;
  setSidebarMobileOpen: (open: boolean) => void;
  registerSidePanelOpen: () => void;
  registerSidePanelClose: () => void;
  toggleTheme: () => void;
}

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem('truspace-theme');
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  localStorage.setItem('truspace-theme', theme);
}

const initialTheme = getInitialTheme();
applyTheme(initialTheme);

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  sidebarMobileOpen: false,
  sidePanelOpenCount: 0,
  theme: initialTheme,

  toggleSidebar: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  setSidebarMobileOpen: (sidebarMobileOpen) =>
    set({ sidebarMobileOpen }),

  registerSidePanelOpen: () =>
    set((state) => ({ sidePanelOpenCount: state.sidePanelOpenCount + 1 })),

  registerSidePanelClose: () =>
    set((state) => ({
      sidePanelOpenCount: Math.max(0, state.sidePanelOpenCount - 1),
    })),

  toggleTheme: () =>
    set((state) => {
      const next: Theme = state.theme === 'light' ? 'dark' : 'light';
      applyTheme(next);
      return { theme: next };
    }),
}));
